/**
 * homingMineSpawner.ts
 * Pool + Poisson spawner + per-tick update for the Level 2-3 homing
 * mine hazard.
 *
 * Sibling of `bossMissileBarrage.ts` — same shape (module-singleton
 * `ObjectPool`, injectable RNG, bulk reset, `updateX` reaper). The
 * mine pool sits parallel to the asteroid/enemy pools because mines
 * are a level-specific hazard rather than a regular spawn family.
 *
 * Owns:
 *  - the `HomingMine` pool (capped at `HOMING_MINE_MAX_ACTIVE`,
 *    pre-warmed at module load),
 *  - `updateHomingMineSpawner()` — single Poisson roll per tick that
 *    acquires + spawns one mine at the right edge of the camera,
 *  - `updateHomingMines()` — integrates motion and reaps dead mines.
 *    Both `updateHomingMineSpawner` and `updateHomingMines` call a
 *    shared internal `sync()` at the top of each tick that releases
 *    the active list on a level-id change OR an `elapsedSeconds`
 *    rewind (scene re-init), so a mid-scene config swap or replay
 *    lands on a clean slate.
 *  - `resetHomingMines()` — bulk-release helper. Called by
 *    `SpaceScene.init()` and `SpaceScene.destroy()` as the primary
 *    scene-lifecycle reset; the in-`sync()` watcher above is a
 *    belt-and-braces backstop for paths that mutate
 *    `spaceState.levelConfig` outside the scene lifecycle.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Level 2-3 nebula gauntlet. See ROADMAP.md line 221.
 *
 * @module homingMineSpawner
 */

import { spaceState } from './spaceState';
import type { LevelRuntime } from './gameState';
import { HomingMine } from '../entities/HomingMine';
import { ObjectPool } from '../utils/objectPool';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import {
  HOMING_MINE_HEIGHT,
  HOMING_MINE_MAX_ACTIVE,
  HOMING_MINE_SPAWN_INTERVAL_S,
  HOMING_MINE_SPAWN_X_OFFSET,
  HOMING_MINE_SPAWN_Y_MIN,
  HOMING_MINE_SPAWN_Y_MAX_OFFSET,
} from '../../constants/spaceEnemyConstants';

/** Reusable empty level for mine update — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Module-level homing-mine pool. Pre-warmed at module load so the
 * first spawn on Level 2-3 pays no allocation cost. Sized to
 * `HOMING_MINE_MAX_ACTIVE` so `acquire()` returning `null` is the
 * spawner's "active cap reached" signal rather than a normal event.
 */
const homingMinePool = new ObjectPool<HomingMine>(
  () => new HomingMine(),
  HOMING_MINE_MAX_ACTIVE,
);
homingMinePool.prewarm(HOMING_MINE_MAX_ACTIVE);

/**
 * Injectable RNG for tests. Defaults to `Math.random`. Production code
 * MUST NOT call `setHomingMineRng` outside tests.
 *
 * @internal
 */
let rng: () => number = Math.random;

/**
 * Replace the spawner's RNG. Test-only seam.
 *
 * @param fn - Zero-arg function returning a number in `[0, 1)`.
 * @internal
 */
export function setHomingMineRng(fn: () => number): void {
  rng = fn;
}

/**
 * Restore the spawner's RNG to `Math.random`. Call from test teardown
 * so pinned sequences do not leak across suites.
 *
 * @internal
 */
export function resetHomingMineRng(): void {
  rng = Math.random;
}

/**
 * Test-only helper: number of mines currently parked in the pool.
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _homingMinePoolAvailable(): number {
  return homingMinePool.available;
}

/**
 * Last level id observed by the spawner. Drives the
 * self-cleanup-on-level-change behaviour. `null` until the first tick.
 *
 * DECISION: Tracked internally rather than reading
 * `spaceState.levelConfig` from the spawner's spawn path because the
 * spawner is gated externally (by `stepSpace`) on the active level
 * already. The transition watcher only needs to fire when the id
 * changes between ticks — which spaceStep doesn't otherwise observe.
 */
let lastLevelId: string | null = null;

/**
 * Last `spaceState.elapsedSeconds` observed by the spawner. Used to
 * detect a scene re-init that lands on the same level id (e.g.
 * `'2-3'` → `'2-3'`). On scene init `elapsedSeconds` resets to 0, so
 * any tick that observes a strict rewind (current < previous) is a
 * fresh scene and the active list must be released to prevent leftover
 * mines from a prior session pinning the pool.
 */
let lastElapsedSeconds = 0;

/**
 * Release every active mine back to the pool and clear the scene's
 * bookkeeping list. Idempotent — safe to call when the active list is
 * already empty. Part of the normal scene lifecycle: `SpaceScene.init()`
 * and `SpaceScene.destroy()` both call this alongside the other
 * `reset*` helpers (`resetShipCombat`, `resetAsteroids`,
 * `resetSpaceEnemies`, `resetBoss`). The in-`sync()` watcher inside
 * this module is a belt-and-braces backstop for level-config swaps or
 * scene-rewind ticks that bypass the scene lifecycle.
 */
export function resetHomingMines(): void {
  for (let i = 0; i < spaceState.mines.length; i++) {
    homingMinePool.release(spaceState.mines[i]);
  }
  spaceState.mines.length = 0;
}

/**
 * Run the level-transition / scene-rewind watcher and update the
 * trackers. Called at the top of BOTH `updateHomingMineSpawner` and
 * `updateHomingMines` so the order in which `stepSpace` invokes them
 * cannot cause a spawn-then-clear race: on the first tick after a
 * level change, the spawner releases stale mines before it tries to
 * acquire a new one, and the updater is then a no-op for the watcher.
 *
 * Two trigger conditions, either of which releases the active list:
 *   1. `spaceState.levelConfig.id` differs from the last observed id
 *      — covers config swaps and scene re-inits onto a different level.
 *   2. `spaceState.elapsedSeconds` is strictly less than the last
 *      observed value — covers a scene re-init that lands on the SAME
 *      level id (e.g. "replay 2-3"), which `initSpaceState` signals by
 *      resetting `elapsedSeconds` to 0.
 */
function sync(): void {
  const cfgId = spaceState.levelConfig.id;
  const elapsed = spaceState.elapsedSeconds;
  if (cfgId !== lastLevelId || elapsed < lastElapsedSeconds) {
    resetHomingMines();
  }
  lastLevelId = cfgId;
  lastElapsedSeconds = elapsed;
}

/**
 * Roll one Poisson spawn tick. Drops the roll when the active mine
 * count is at the cap or the pool is exhausted.
 *
 * DECISION: This module does NOT gate on level id — `stepSpace` is the
 * sole caller and already gates on `'2-3'` + `spawnGate`. Keeping the
 * gate at the call site mirrors the asteroid/enemy spawners and keeps
 * this function trivially testable (call it and assert it spawned).
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateHomingMineSpawner(dt: number): void {
  sync();
  if (spaceState.mines.length >= HOMING_MINE_MAX_ACTIVE) return;

  const p = dt / HOMING_MINE_SPAWN_INTERVAL_S;
  if (rng() >= p) return;

  const spawnX = spaceState.cameraX + VIEWPORT_WIDTH + HOMING_MINE_SPAWN_X_OFFSET;
  const yMax = VIEWPORT_HEIGHT - HOMING_MINE_HEIGHT - HOMING_MINE_SPAWN_Y_MAX_OFFSET;
  const yRange = Math.max(0, yMax - HOMING_MINE_SPAWN_Y_MIN);
  const spawnY = HOMING_MINE_SPAWN_Y_MIN + Math.floor(rng() * (yRange + 1));

  const mine = homingMinePool.acquire();
  if (mine === null) return; // Pool exhausted — drop the spawn silently.
  mine.spawn(spawnX, spawnY);
  spaceState.mines.push(mine);
}

/**
 * Update every active mine and release dead ones back to the pool.
 * Also runs the level-transition / scene-rewind watcher via `sync()`
 * so a scene that re-inits onto a non-2-3 level — OR onto the same
 * level id (replay) — starts mine-free without any explicit
 * SpaceScene wiring.
 *
 * Always called unconditionally from `stepSpace` so the watcher fires
 * on every tick regardless of the spawner's level gate. When the
 * active list is empty (the common case off 2-3) the work is one
 * comparison + a no-op loop.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateHomingMines(dt: number): void {
  sync();

  const arr = spaceState.mines;
  for (let i = arr.length - 1; i >= 0; i--) {
    const m = arr[i];
    m.update(dt, EMPTY_SPACE_LEVEL);
    if (m.dead) {
      homingMinePool.release(m);
      arr.splice(i, 1);
    }
  }
}

/**
 * Test-only helper: clear the internal trackers so the next call to
 * `updateHomingMineSpawner` or `updateHomingMines` re-runs the
 * transition watcher. Tests use this in `beforeEach` so each case
 * starts from a deterministic state.
 *
 * @internal
 */
export function _resetHomingMineSpawnerState(): void {
  lastLevelId = null;
  lastElapsedSeconds = 0;
  resetHomingMines();
}
